/**
 * One item in the catalogue: picture, name, price, size, quantity and
 * "Add to order". Adding the same item and size again raises that line's
 * quantity (the page merges it). "Add" is disabled, with the reason under it,
 * when the addition would take the order over the voucher or past 10.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { Check, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  CHOOSE_SIZE_FIRST,
  addBlockedReason,
  formatRand,
  priceLabel,
  pricesDiffer,
  sizeOptionLabel,
  sizePrice,
  type DraftLine,
} from '@/lib/staffOrder'
import type { StaffOrderItem } from '@/services/api/staffOrderService'

import { ItemPicture } from './ItemPicture'
import { QuantityStepper } from './QuantityStepper'
import { selectClass } from './styles'

const LONG_DESCRIPTION = 140

interface CatalogueCardProps {
  item: StaffOrderItem
  lines: DraftLine[]
  voucherCents: number
  onAdd: (item: StaffOrderItem, size: string, quantity: number) => void
}

export function CatalogueCard({ item, lines, voucherCents, onAdd }: CatalogueCardProps) {
  const singleSize = item.sizes.length === 1
  const [size, setSize] = useState(singleSize ? item.sizes[0].size : '')
  const [quantity, setQuantity] = useState(1)
  const [askForSize, setAskForSize] = useState(false)
  const [justAdded, setJustAdded] = useState(false)
  const [showFullDescription, setShowFullDescription] = useState(false)
  const sizeRef = useRef<HTMLSelectElement | null>(null)
  const sizeId = useId()
  const reasonId = useId()

  useEffect(() => {
    if (!justAdded) return
    const timer = window.setTimeout(() => setJustAdded(false), 2000)
    return () => window.clearTimeout(timer)
  }, [justAdded])

  const reason = addBlockedReason({ lines, voucherCents, item, size, quantity })
  const needsSize = reason === CHOOSE_SIZE_FIRST
  const blocked = !!reason && !needsSize
  const chosenPrice = size ? sizePrice(item, size) : null
  const price = chosenPrice !== null && pricesDiffer(item) ? formatRand(chosenPrice) : priceLabel(item)
  const inOrder = lines.filter((l) => l.item_id === item.id).reduce((n, l) => n + l.quantity, 0)
  const description = item.description?.trim() || ''
  const longDescription = description.length > LONG_DESCRIPTION

  const handleAdd = () => {
    if (needsSize) {
      setAskForSize(true)
      sizeRef.current?.focus()
      return
    }
    if (reason) return
    onAdd(item, size, quantity)
    setQuantity(1)
    setAskForSize(false)
    setJustAdded(true)
  }

  const hint = blocked ? reason : askForSize && needsSize ? CHOOSE_SIZE_FIRST : null

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="relative border-b border-gray-100">
        <ItemPicture src={item.image_url} alt={item.name} className="aspect-[4/3] w-full p-2" />
        {inOrder > 0 && (
          <span className="absolute left-2 top-2 rounded-full bg-heritage-blue px-2 py-0.5 text-xs font-medium text-white">
            {inOrder} in your order
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold leading-snug text-heritage-blue-700">{item.name}</h3>
          <p className="text-base font-semibold tabular-nums text-gray-900">{price}</p>
          {description && (
            <p className={`text-xs leading-relaxed text-gray-500 ${longDescription && !showFullDescription ? 'line-clamp-3' : ''}`}>
              {description}
            </p>
          )}
          {longDescription && (
            <button
              type="button"
              className="text-xs font-medium text-future-blue-700 hover:underline"
              onClick={() => setShowFullDescription((v) => !v)}
            >
              {showFullDescription ? 'Show less' : 'Read more'}
            </button>
          )}
          {item.note && (
            <p className="rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">{item.note}</p>
          )}
        </div>

        <div className="mt-auto space-y-2">
          {item.sizes.length === 0 ? null : singleSize ? (
            <p className="text-sm text-gray-600">
              Size: <span className="font-medium text-heritage-blue-700">{item.sizes[0].size}</span>
            </p>
          ) : (
            <div className="space-y-1">
              <label htmlFor={sizeId} className="text-xs font-medium text-gray-600">
                Size
              </label>
              <select
                id={sizeId}
                ref={sizeRef}
                className={selectClass}
                value={size}
                aria-invalid={askForSize && needsSize ? true : undefined}
                onChange={(e) => {
                  setSize(e.target.value)
                  setAskForSize(false)
                }}
              >
                <option value="" disabled>
                  Choose a size
                </option>
                {item.sizes.map((s) => (
                  <option key={s.size} value={s.size}>
                    {sizeOptionLabel(item, s)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <QuantityStepper
              value={quantity}
              onChange={(q) => setQuantity(q)}
              label={`Quantity of ${item.name}`}
              disabled={item.sizes.length === 0}
            />
            <Button
              type="button"
              className={`h-10 min-w-[8.5rem] flex-1 ${justAdded ? 'bg-emerald-600 hover:bg-emerald-600' : ''}`}
              onClick={handleAdd}
              disabled={blocked}
              aria-describedby={hint ? reasonId : undefined}
            >
              {justAdded ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {justAdded ? 'Added' : 'Add to order'}
            </Button>
          </div>
          {hint && (
            <p id={reasonId} className={`text-xs ${blocked ? 'text-gray-500' : 'text-red-600'}`}>
              {hint}
            </p>
          )}
        </div>
      </div>
    </article>
  )
}
